import { Injectable, inject } from '@angular/core';

import { firstValueFrom } from 'rxjs';
import { defaultIfEmpty, map } from 'rxjs/operators';

import { RtDialogRef, RtDialogService } from '@rt-tools/ui-kit-v2';

import { SignInModalComponent } from './sign-in-modal.component';

/**
 * Opens the sign-in window over the current route. Pages that need auth
 * (e.g. /projects, "Save project") call `requireSession()` and await the
 * resolved boolean: `true` when the user signed in, `false` when they
 * dismissed — by the cancel button, the backdrop or Escape. The window is the
 * kit dialog opened by its service, so it never owns its own URL.
 */
@Injectable({ providedIn: 'root' })
export class AuthDialogService {
    readonly #dialogs: RtDialogService = inject(RtDialogService);
    #open: boolean = false;

    public async requireSession(): Promise<boolean> {
        if (this.#open) {
            return false;
        }
        this.#open = true;
        const ref: RtDialogRef<boolean> = this.#dialogs.open<SignInModalComponent, null, boolean>(SignInModalComponent);
        try {
            return await firstValueFrom(
                ref.afterClosed().pipe(
                    map((signedIn: boolean | undefined = false): boolean => signedIn),
                    defaultIfEmpty(false)
                )
            );
        } finally {
            this.#open = false;
        }
    }
}
